import React, { useEffect, useState } from 'react';
import {
  listAllTenanciesWithActivity,
  TenancyWithActivity,
  TenancyUser,
  TenancyAction,
  TenancyCohort,
  TenancyMatch,
} from '@services/cloudFunctions';
import {
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
} from '@chakra-ui/accordion';

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

const ACTION_LABELS: Record<string, string> = {
  selectParticipants: 'Select Participants',
  welcomePackage: 'Welcome Package',
  createMatches: 'Create Matches',
  launch: 'Launch',
  collectFeedback: 'Collect Feedback',
  sendMatches: 'Send Matches',
  sendIntroConsent: 'Intro Consent',
  sendCheckIn: 'Check-In',
  connectionEmail: 'Connection Email',
  feedbackRequest: 'Feedback Request',
};

const formatActionName = (action: string): string => ACTION_LABELS[action] || action;

const formatDate = (date: number | null | undefined): string => {
  if (!date) return 'N/A';
  try {
    const dateObj = new Date(date);
    if (isNaN(dateObj.getTime())) return 'N/A';
    return dateObj.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return 'N/A';
  }
};

const STATUS_STYLES: Record<string, string> = {
  completed: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  active: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  draft: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
  terminated: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
};

const getStatusStyles = (status: string): string =>
  STATUS_STYLES[status] || STATUS_STYLES.draft;

// ============================================================================
// REUSABLE COMPONENTS
// ============================================================================

const Badge: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className = '',
}) => (
  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${className}`}>
    {children}
  </span>
);

const CollapsibleSection: React.FC<{
  title: string;
  count: number;
  defaultOpen?: boolean;
  forceOpen?: boolean;
  children: React.ReactNode;
}> = ({ title, count, defaultOpen = false, forceOpen, children }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  useEffect(() => {
    if (forceOpen) setIsOpen(true);
  }, [forceOpen]);

  return (
    <div className="border-t border-gray-200 dark:border-navy-600">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-6 py-3 bg-gray-50 dark:bg-navy-900 flex items-center justify-between hover:bg-gray-100 dark:hover:bg-navy-800 transition-colors"
      >
        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
          {title} ({count})
        </h4>
        <span className="text-gray-500 dark:text-gray-400 text-lg font-medium">
          {isOpen ? '−' : '+'}
        </span>
      </button>
      {isOpen && children}
    </div>
  );
};

// ============================================================================
// PAGINATED TABLE
// ============================================================================

interface PaginatedTableProps<T> {
  items: T[];
  pageSize?: number;
  searchPlaceholder?: string;
  searchFilter: (item: T, query: string) => boolean;
  headers: string[];
  renderRow: (item: T, index: number) => React.ReactNode;
  emptyMessage: string;
  getKey: (item: T, index: number) => string;
}

function PaginatedTable<T>({
  items,
  pageSize = 10,
  searchPlaceholder = 'Search...',
  searchFilter,
  headers,
  renderRow,
  emptyMessage,
  getKey,
}: PaginatedTableProps<T>) {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);

  const filteredItems = search
    ? items.filter((item) => searchFilter(item, search.toLowerCase()))
    : items;

  const totalPages = Math.ceil(filteredItems.length / pageSize);
  const paginatedItems = filteredItems.slice(page * pageSize, (page + 1) * pageSize);

  useEffect(() => {
    setPage(0);
  }, [search]);

  if (items.length === 0) {
    return (
      <div className="px-6 py-4 text-center text-gray-500 dark:text-gray-400 text-sm">
        {emptyMessage}
      </div>
    );
  }

  return (
    <div>
      {/* Search */}
      <div className="px-4 py-2 border-b border-gray-200 dark:border-navy-600">
        <input
          type="text"
          placeholder={searchPlaceholder}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-navy-500 rounded-lg bg-white dark:bg-navy-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-500"
        />
      </div>

      {/* Table */}
      {paginatedItems.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead>
              <tr className="bg-gray-100 dark:bg-navy-900">
                {headers.map((header) => (
                  <th
                    key={header}
                    className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                  >
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {paginatedItems.map((item, index) => (
                <React.Fragment key={getKey(item, index)}>
                  {renderRow(item, index)}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="px-6 py-4 text-center text-gray-500 dark:text-gray-400 text-sm">
          No results found
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="px-4 py-2 flex items-center justify-between border-t border-gray-200 dark:border-navy-600 bg-gray-50 dark:bg-navy-900">
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {filteredItems.length} total · Page {page + 1} of {totalPages}
          </span>
          <div className="flex gap-1">
            <button
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
              className="px-2 py-1 text-xs rounded bg-gray-200 dark:bg-navy-700 text-gray-700 dark:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-300 dark:hover:bg-navy-600"
            >
              Prev
            </button>
            <button
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
              className="px-2 py-1 text-xs rounded bg-gray-200 dark:bg-navy-700 text-gray-700 dark:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-300 dark:hover:bg-navy-600"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// TABLE ROW COMPONENTS
// ============================================================================

const rowClassName = (index: number) =>
  index % 2 === 0 ? 'bg-gray-50 dark:bg-navy-800' : 'bg-white dark:bg-navy-900';

const UserRow: React.FC<{ user: TenancyUser; index: number }> = ({ user, index }) => (
  <tr className={rowClassName(index)}>
    <td className="px-4 py-2 text-sm text-gray-900 dark:text-white">
      {[user.firstName, user.lastName].filter(Boolean).join(' ') || 'Unknown'}
    </td>
    <td className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400">{user.email || 'N/A'}</td>
    <td className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400">{user.role}</td>
    <td className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400">
      {formatDate(user.lastCohortAction)}
    </td>
  </tr>
);

const ActionRow: React.FC<{ action: TenancyAction; index: number }> = ({ action, index }) => (
  <tr className={rowClassName(index)}>
    <td className="px-4 py-2 text-sm text-gray-900 dark:text-white">
      {formatActionName(action.action)}
    </td>
    <td className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400">{action.status}</td>
    <td className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400">{action.cohortTitle}</td>
    <td className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400">{formatDate(action.date)}</td>
  </tr>
);

interface MatchWithCohort extends TenancyMatch {
  cohortTitle: string;
}

const MatchRow: React.FC<{ match: MatchWithCohort; index: number; profileMap: Record<string, TenancyUser> }> = ({
  match,
  index,
  profileMap
}) => {
  const mentor = profileMap[match.mentorId || ''];
  const mentee = profileMap[match.menteeId || ''];
  const mentorName = mentor ? [mentor.firstName, mentor.lastName].filter(Boolean).join(' ') : match.mentorId || 'Unknown';
  const menteeName = mentee ? [mentee.firstName, mentee.lastName].filter(Boolean).join(' ') : match.menteeId || 'Unknown';

  return (
    <tr className={rowClassName(index)}>
      <td className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400">{match.cohortTitle}</td>
      <td className="px-4 py-2 text-sm text-gray-900 dark:text-white">{mentorName}</td>
      <td className="px-4 py-2 text-sm text-gray-900 dark:text-white">{menteeName}</td>
      <td className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400">
        {match.matchQuality ? `${(match.matchQuality * 100).toFixed(0)}%` : '-'}
      </td>
      <td className="px-4 py-2">
        <Badge className={match.hasFeedback ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'}>
          {match.hasFeedback ? 'Yes' : 'No'}
        </Badge>
      </td>
      <td className="px-4 py-2 text-sm text-gray-500">{formatDate(match.createdAt)}</td>
    </tr>
  );
};

const CohortRow: React.FC<{ cohort: TenancyCohort; index: number }> = ({ cohort, index }) => (
  <tr className={rowClassName(index)}>
    <td className="px-4 py-2 text-sm text-gray-900 dark:text-white">{cohort.title}</td>
    <td className="px-4 py-2">
      <Badge className={getStatusStyles(cohort.status)}>{cohort.status}</Badge>
    </td>
    <td className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400">{cohort.userCount}</td>
    <td className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400">{cohort.matches.length}</td>
    <td className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400">
      {cohort.workflowType || '-'}
    </td>
    <td className="px-4 py-2 text-sm text-gray-500">{formatDate(cohort.createdAt)}</td>
  </tr>
);

// ============================================================================
// TENANCY CARD
// ============================================================================

const TenancyCard: React.FC<{ tenancy: TenancyWithActivity }> = ({ tenancy }) => {
  const [hasExpanded, setHasExpanded] = useState(false);
  const lastAction = tenancy.actions[0] ?? null;

  // Build a profile lookup map for displaying match names
  const profileMap = tenancy.users.reduce((map, user) => {
    map[user.uid] = user;
    return map;
  }, {} as Record<string, TenancyUser>);

  // Aggregate all matches across cohorts
  const allMatches: MatchWithCohort[] = tenancy.cohorts.flatMap((cohort) =>
    cohort.matches.map((match) => ({ ...match, cohortTitle: cohort.title }))
  );
  const totalMatches = allMatches.length;

  return (
    <AccordionItem className="mb-4 rounded-xl border border-gray-200 dark:border-navy-600 bg-white dark:bg-navy-800 overflow-hidden">
      <AccordionButton
        className="w-full px-6 py-4 hover:bg-gray-50 dark:hover:bg-navy-700 flex items-center justify-between"
        onClick={() => setHasExpanded(true)}
      >
        <div className="flex-1 text-left">
          {/* Title Row */}
          <div className="flex items-center gap-3 mb-2">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{tenancy.title}</h3>
            {tenancy.disabled && <Badge className="bg-red-500 text-white">Disabled</Badge>}
            <Badge className="bg-gray-100 text-gray-600 dark:bg-navy-700 dark:text-gray-300">
              {tenancy.actions.length} actions
            </Badge>
          </div>

          {/* Meta Row */}
          <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 dark:text-gray-400">
            <span>Admin: {tenancy.adminEmail || 'N/A'}</span>
            <span className="text-gray-300 dark:text-navy-500">|</span>
            <span>{tenancy.userCount} users</span>
            <span className="text-gray-300 dark:text-navy-500">|</span>
            <span>{tenancy.cohortCount} cohorts</span>
            <span className="text-gray-300 dark:text-navy-500">|</span>
            <span>{totalMatches} matches</span>
          </div>

          {/* Last Action */}
          <div className="mt-3 flex flex-wrap items-center gap-2">
            {lastAction ? (
              <>
                <span className="text-xs text-gray-500">Last:</span>
                <Badge className={getStatusStyles(lastAction.status)}>
                  {formatActionName(lastAction.action)}
                </Badge>
                <span className="text-xs text-gray-500">in {lastAction.cohortTitle}</span>
                <span className="text-xs text-gray-400">{formatDate(lastAction.date)}</span>
              </>
            ) : (
              <span className="text-xs text-gray-400">No actions yet</span>
            )}
          </div>
        </div>
        <AccordionIcon className="text-gray-400 ml-4" />
      </AccordionButton>

      <AccordionPanel pb={0} px={0}>
        {/* Cohorts */}
        <CollapsibleSection
          title="Cohorts"
          count={tenancy.cohorts.length}
          forceOpen={hasExpanded}
        >
          {tenancy.cohorts.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className="bg-gray-100 dark:bg-navy-900">
                    {['Title', 'Status', 'Users', 'Matches', 'Workflow', 'Created'].map((h) => (
                      <th
                        key={h}
                        className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {tenancy.cohorts.map((cohort, i) => (
                    <CohortRow key={cohort.id} cohort={cohort} index={i} />
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="px-6 py-4 text-center text-gray-500 text-sm">No cohorts created</div>
          )}
        </CollapsibleSection>

        {/* Actions */}
        <CollapsibleSection
          title="Actions"
          count={tenancy.actions.length}
          forceOpen={hasExpanded}
        >
          <PaginatedTable
            items={tenancy.actions}
            headers={['Action', 'Status', 'Cohort', 'Date']}
            searchPlaceholder="Search actions..."
            searchFilter={(action, query) =>
              formatActionName(action.action).toLowerCase().includes(query) ||
              action.status.toLowerCase().includes(query) ||
              action.cohortTitle.toLowerCase().includes(query)
            }
            renderRow={(action, i) => <ActionRow action={action} index={i} />}
            emptyMessage="No actions recorded"
            getKey={(action, i) => `${action.cohortId}-${action.action}-${i}`}
          />
        </CollapsibleSection>

        {/* Matches */}
        <CollapsibleSection title="Matches" count={totalMatches} forceOpen={hasExpanded}>
          <PaginatedTable
            items={allMatches}
            headers={['Cohort', 'Mentor', 'Mentee', 'Quality', 'Feedback', 'Created']}
            searchPlaceholder="Search matches..."
            searchFilter={(match, query) => {
              const mentor = profileMap[match.mentorId || ''];
              const mentee = profileMap[match.menteeId || ''];
              const mentorName = mentor ? `${mentor.firstName || ''} ${mentor.lastName || ''}`.toLowerCase() : '';
              const menteeName = mentee ? `${mentee.firstName || ''} ${mentee.lastName || ''}`.toLowerCase() : '';
              return mentorName.includes(query) || menteeName.includes(query) || (match.cohortTitle || '').toLowerCase().includes(query);
            }}
            renderRow={(match, i) => <MatchRow match={match} index={i} profileMap={profileMap} />}
            emptyMessage="No matches created"
            getKey={(match) => match.id}
          />
        </CollapsibleSection>

        {/* Users */}
        <CollapsibleSection title="Users" count={tenancy.users.length} forceOpen={hasExpanded}>
          <PaginatedTable
            items={tenancy.users}
            headers={['Name', 'Email', 'Role', 'Last Action']}
            searchPlaceholder="Search users..."
            searchFilter={(user, query) =>
              (user.firstName || '').toLowerCase().includes(query) ||
              (user.lastName || '').toLowerCase().includes(query) ||
              (user.email || '').toLowerCase().includes(query) ||
              user.role.toLowerCase().includes(query)
            }
            renderRow={(user, i) => <UserRow user={user} index={i} />}
            emptyMessage="No users in this tenancy"
            getKey={(user) => user.uid}
          />
        </CollapsibleSection>
      </AccordionPanel>
    </AccordionItem>
  );
};

// ============================================================================
// ADMIN GROUP HELPERS
// ============================================================================

type GroupedTenancies = Record<string, TenancyWithActivity[]>;

const groupByAdmin = (tenancies: TenancyWithActivity[]): GroupedTenancies =>
  tenancies.reduce((groups, tenancy) => {
    const admin = tenancy.adminEmail || 'No Admin';
    (groups[admin] ??= []).push(tenancy);
    return groups;
  }, {} as GroupedTenancies);

const getSortedAdminGroups = (grouped: GroupedTenancies): [string, TenancyWithActivity[]][] =>
  Object.entries(grouped).sort(([, a], [, b]) => {
    const maxA = Math.max(...a.map((t) => t.lastActionDate || 0));
    const maxB = Math.max(...b.map((t) => t.lastActionDate || 0));
    return maxB - maxA;
  });

// ============================================================================
// STATS CARD
// ============================================================================

const StatCard: React.FC<{ value: number; label: string; color: string }> = ({
  value,
  label,
  color,
}) => (
  <div className="rounded-xl bg-white dark:bg-navy-800 p-4 border border-gray-200 dark:border-navy-600">
    <div className={`text-2xl font-bold ${color}`}>{value}</div>
    <div className="text-sm text-gray-600 dark:text-gray-400">{label}</div>
  </div>
);

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function AllTenanciesList() {
  const [tenancies, setTenancies] = useState<TenancyWithActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [groupByAdminEnabled, setGroupByAdminEnabled] = useState(true);

  useEffect(() => {
    const fetchTenancies = async () => {
      try {
        setLoading(true);
        setError(null);
        const result = await listAllTenanciesWithActivity();
        if (result.success && result.data) {
          setTenancies(result.data);
        } else {
          setError(result.error || 'Failed to fetch tenancies');
        }
      } catch (err: any) {
        console.error('Error fetching tenancies:', err);
        setError(err.message || 'An unexpected error occurred');
      } finally {
        setLoading(false);
      }
    };
    fetchTenancies();
  }, []);

  const groupedTenancies = groupByAdmin(tenancies);
  const sortedAdminGroups = getSortedAdminGroups(groupedTenancies);
  const adminCount = Object.keys(groupedTenancies).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-500 border-t-transparent mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">Loading tenancies...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-xl border border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/20 p-6">
        <h3 className="text-lg font-semibold text-red-800 dark:text-red-200 mb-2">
          Error Loading Tenancies
        </h3>
        <p className="text-red-600 dark:text-red-300">{error}</p>
        <button
          onClick={() => window.location.reload()}
          className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">All Tenancies</h1>
          <p className="text-gray-600 dark:text-gray-400">
            {tenancies.length} tenancies across {adminCount} admins
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600 dark:text-gray-400">Group by admin</span>
          <button
            onClick={() => setGroupByAdminEnabled(!groupByAdminEnabled)}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              groupByAdminEnabled ? 'bg-brand-500' : 'bg-gray-300 dark:bg-navy-600'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                groupByAdminEnabled ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-6">
        <StatCard value={tenancies.length} label="Total Tenancies" color="text-brand-500" />
        <StatCard value={adminCount} label="Admins" color="text-orange-500" />
        <StatCard
          value={tenancies.filter((t) => !t.disabled).length}
          label="Active"
          color="text-green-500"
        />
        <StatCard
          value={tenancies.reduce((sum, t) => sum + t.userCount, 0)}
          label="Total Users"
          color="text-purple-500"
        />
        <StatCard
          value={tenancies.reduce((sum, t) => sum + t.cohortCount, 0)}
          label="Total Cohorts"
          color="text-blue-500"
        />
        <StatCard
          value={tenancies.reduce((sum, t) => sum + t.cohorts.reduce((s, c) => s + c.matches.length, 0), 0)}
          label="Total Matches"
          color="text-pink-500"
        />
      </div>

      {/* Tenancy List */}
      {tenancies.length === 0 ? (
        <div className="rounded-xl border border-gray-200 dark:border-navy-600 bg-white dark:bg-navy-800 p-12 text-center">
          <p className="text-gray-600 dark:text-gray-400">No tenancies found</p>
        </div>
      ) : groupByAdminEnabled ? (
        <div className="space-y-8">
          {sortedAdminGroups.map(([adminEmail, adminTenancies]) => (
            <div key={adminEmail}>
              <div className="mb-4 flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-100 dark:bg-brand-900">
                  <span className="text-sm font-bold text-brand-600 dark:text-brand-200">
                    {adminEmail.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                    {adminEmail}
                  </h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {adminTenancies.length} {adminTenancies.length === 1 ? 'tenancy' : 'tenancies'} ·{' '}
                    {adminTenancies.reduce((sum, t) => sum + t.userCount, 0)} users
                  </p>
                </div>
              </div>
              <Accordion allowMultiple>
                {adminTenancies.map((tenancy) => (
                  <TenancyCard key={tenancy.id} tenancy={tenancy} />
                ))}
              </Accordion>
            </div>
          ))}
        </div>
      ) : (
        <Accordion allowMultiple>
          {tenancies.map((tenancy) => (
            <TenancyCard key={tenancy.id} tenancy={tenancy} />
          ))}
        </Accordion>
      )}
    </div>
  );
}
